import React, {useMemo, useState} from 'react';
import UnscheduledTravelers from './UnscheduledTravelers';
import {useTheme} from "./ThemeContext";
import {Department, ITraveler} from "../data/interfaces";
import {Button, Form} from 'react-bootstrap';
import {MdCancel} from 'react-icons/md';
import {filterUnscheduledTravelersBySearch} from '../utils/unscheduledTravelerSearch';
import {compareTravelersByReadyToShipDeadline} from '../utils/travelerSorting';


interface SidebarProps {
  isCollapsed: boolean;
  department: Department;
  unscheduledTravelers: ITraveler[];
  onTravelerClick: (travelerId: string) => void;
  onHideTraveler?: (traveler: ITraveler) => void;
}

/**
 * Sidebar component to display unscheduled travelers.
 * @param isCollapsed
 * @constructor
 */

const Sidebar: React.FC<SidebarProps> = ({isCollapsed, department, unscheduledTravelers, onTravelerClick, onHideTraveler}) => {
  const {theme} = useTheme();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredUnscheduledTravelers = useMemo(() => {
    const sortedUnscheduledTravelers = [...unscheduledTravelers].sort(compareTravelersByReadyToShipDeadline);
    return filterUnscheduledTravelersBySearch(sortedUnscheduledTravelers, searchQuery);
  }, [searchQuery, unscheduledTravelers]);

  const hasActiveSearch = searchQuery.trim().length > 0;
  const emptyMessage = hasActiveSearch
    ? 'No unscheduled travelers match this search.'
    : undefined;


  return (
    <>
      <div
        className={`calendar-sidebar min-vh-100 h-100 w-100 ${isCollapsed ? 'd-none' : 'd-block'} ${
          theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'
        } d-flex flex-column overflow-hidden`}
        style={{height: '100vh'}}
      >
        <div className="unscheduled-travelers-header">
          <div className="pt-2 text-center">
            <h3>Unscheduled Travelers</h3>
          </div>
          <div className="px-3 pb-2">
            <div className="position-relative">
              <Form.Control
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search travelers"
                aria-label="Search unscheduled travelers"
                className="pe-5 unscheduled-traveler-search-input"
              />
              {searchQuery && (
                <Button
                  type="button"
                  variant="link"
                  className="position-absolute top-50 end-0 translate-middle-y pe-2 unscheduled-traveler-search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear traveler search"
                >
                  <MdCancel/>
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="unscheduled-travelers-scroll flex-grow-1 overflow-auto">
           <UnscheduledTravelers
             department={department}
             unscheduledTravelers={filteredUnscheduledTravelers}
             onTravelerClick={onTravelerClick}
             onHideTraveler={onHideTraveler}
             emptyMessage={emptyMessage}
             searchQuery={searchQuery}
           />
        </div>
      </div>
    </>
  );
};

export default Sidebar;
